/**
 * 일지 복사
 * ---------------------------------------------------------------------------
 * 현장에서 가장 많이 쓰는 기능이다. 대부분의 날은 같은 시설이 같은 시간대로
 * 돈다.
 *
 * 왜 여러 날짜인가
 *   며칠 혹은 한 달을 밀린 채로 발견하는 일이 흔하다. 하루씩 복사하면 서른
 *   번을 눌러야 하고, 그 사이에 하루를 빠뜨린다.
 *
 * 세 가지 방법으로 날짜를 고른다
 *   구간      "3일부터 20일까지" — 밀린 기간을 통째로
 *   빠른 선택 "이번 달", "지난 달", "최근 7일" — 가장 흔한 경우
 *   개별      달력에서 직접. 주말을 빼는 등 예외를 다룬다
 *
 * 무엇이 복사되는가
 *   날짜·날씨·기온·확인자를 뺀 모든 값이다. 측정값과 수량도 가져간다 —
 *   현장에서는 비슷한 날을 한꺼번에 채우고 다른 것만 고친다.
 *
 * 날씨
 *   기본으로 켜 둔다. 원본의 날씨를 그대로 복사하면 한 달 내내 같은 날씨인
 *   일지가 만들어지고, 그건 사실이 아니다. 대상일의 값을 못 받으면 원본
 *   값을 그대로 둔다.
 */

import { useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Modal, Space, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

const { Text } = Typography;

/** 한 번에 복사할 수 있는 최대 날짜 수. 서버 스키마와 같아야 한다. */
const MAX_DATES = 62;

export interface CopyPlan {
  targetDates: string[];
  applyWeather: boolean;
}

export function CopyDialog({
  open,
  sourceDate,
  loading,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  sourceDate: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (plan: CopyPlan) => void;
}) {
  const [dates, setDates] = useState<Dayjs[]>([]);
  const [applyWeather, setApplyWeather] = useState(true);

  /** 이미 고른 날짜와 합친다. 구간을 두 번 더할 수 있어야 한다. */
  const merge = (added: Dayjs[]) => {
    const keyed = new Map<string, Dayjs>();
    for (const d of [...dates, ...added]) {
      const key = d.format('YYYY-MM-DD');
      // 원본 자신은 뺀다. 복사해 봐야 "이미 있는 날짜" 로 걸린다.
      if (key !== sourceDate) keyed.set(key, d);
    }
    setDates([...keyed.values()].sort((a, b) => a.valueOf() - b.valueOf()).slice(0, MAX_DATES));
  };

  const addRange = (start: Dayjs, end: Dayjs) => {
    const added: Dayjs[] = [];
    let cursor = start.startOf('day');
    const last = end.startOf('day');
    while (!cursor.isAfter(last) && added.length <= MAX_DATES) {
      added.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    merge(added);
  };

  const source = dayjs(sourceDate);

  return (
    <Modal
      open={open}
      title="일지 복사"
      okText={dates.length > 0 ? `${dates.length}일 복사` : '복사'}
      cancelText="취소"
      okButtonProps={{ disabled: dates.length === 0, loading }}
      onCancel={onCancel}
      onOk={() =>
        onConfirm({
          targetDates: dates.map((d) => d.format('YYYY-MM-DD')),
          applyWeather,
        })
      }
      width={620}
      destroyOnHidden
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Text type="secondary">
          <Tag>{sourceDate}</Tag> 일지의 내용을 아래 날짜로 그대로 복사합니다. 날짜와
          날씨·기온은 대상일 것으로 바뀌고, 확인자는 사업장 기본 확인자로 들어갑니다. 복사한
          뒤 그날 달라진 값만 고치세요.
        </Text>

        <div>
          <Text strong>구간으로 고르기</Text>
          <DatePicker.RangePicker
            style={{ width: '100%', marginTop: 6 }}
            onChange={(v) => {
              if (v?.[0] && v[1]) addRange(v[0], v[1]);
            }}
          />
        </div>

        <Space wrap size={6}>
          <Button size="small" onClick={() => addRange(source.add(1, 'day'), source.add(7, 'day'))}>
            다음 7일
          </Button>
          <Button
            size="small"
            onClick={() => addRange(source.startOf('month'), source.endOf('month'))}
          >
            이번 달 전체
          </Button>
          <Button
            size="small"
            onClick={() =>
              addRange(
                source.subtract(1, 'month').startOf('month'),
                source.subtract(1, 'month').endOf('month'),
              )
            }
          >
            지난 달 전체
          </Button>
          <Button size="small" danger disabled={dates.length === 0} onClick={() => setDates([])}>
            모두 지우기
          </Button>
        </Space>

        <div>
          <Text strong>개별로 고르기 · 확인</Text>
          <DatePicker
            multiple
            style={{ width: '100%', marginTop: 6 }}
            maxTagCount={10}
            value={dates}
            onChange={(v) => setDates((v ?? []).filter((d) => d.format('YYYY-MM-DD') !== sourceDate))}
            placeholder="날짜를 고르거나 위에서 구간을 더하세요"
          />
        </div>

        <Checkbox checked={applyWeather} onChange={(e) => setApplyWeather(e.target.checked)}>
          대상일의 날씨·기온을 사업장 주소지 기준으로 다시 받아 채웁니다
          <Text type="secondary"> (받지 못하면 원본 값을 그대로 둡니다)</Text>
        </Checkbox>

        {dates.length >= MAX_DATES && (
          <Alert
            type="warning"
            showIcon
            message={`한 번에 복사할 수 있는 날짜는 ${MAX_DATES}일까지입니다.`}
          />
        )}
      </Space>
    </Modal>
  );
}
